import type { PureMeasureComponentProps, PureMeasureComponentState } from "@component/PureMeasureComponent";
import { PureMeasureComponent } from "@component/PureMeasureComponent";
import {
	faArrowRight as fasArrowRight,
	faArrowLeft as fasArrowLeft,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import type { GA4EventParameters } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { Button, ProgressButton, PromisePool, findErrorInput, isFocusableInput } from "bm-ui-kit";
import type { ProgressButtonState } from "bm-ui-kit";
import type { FormApi, FormState } from "final-form";
import createDecorator from "final-form-focus";
import compact from "lodash/compact";
import React from "react";
import type { ReactNode } from "react";
import { Col, Container, Form, Row } from "react-bootstrap";
import { Form as FinalForm, FormSpy } from "react-final-form";
import type { StepByStepContainerContextType } from "./StepByStepContainerContext";
import { StepByStepContainerContext } from "./StepByStepContainerContext";

type HTMLInput = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

interface HasError {
	(stepName: string): void;
}

export interface BasicStepProps extends PureMeasureComponentProps {
	className?: string;

	stepName: string;
	defaultTarget?: string;

	stepNumber: number;
	submitState?: ProgressButtonState;
	leadId?: number;
	previousStep?: string;

	showBackButton?: boolean;

	buttonNextText?: string;
	buttonBackText?: string;
	buttonNextFaIcon?: ReactNode;
	buttonBackFaIcon?: ReactNode;
	buttonNextClassName?: string;
	iconPosition?: string;
	modifiedProgressButtonSize?: boolean;

	hasError: HasError;
}

export interface BasicStepState extends PureMeasureComponentState {}

export abstract class BasicStep<
	FormFields extends Record<string, any> = {},
	Props extends BasicStepProps = BasicStepProps,
	State extends BasicStepState = BasicStepState,
> extends PureMeasureComponent<Props, State> {
	public static contextType = StepByStepContainerContext;
	public readonly context: React.ContextType<typeof StepByStepContainerContext>;
	private target: string = undefined;
	private form: FormApi<FormFields> = undefined;
	private readonly formRef: React.RefObject<HTMLFormElement> = React.createRef();
	private readonly focusOnError: any;
	protected readonly promisePool = new PromisePool();
	protected errors: Array<string> = [];

	public constructor(props: Props) {
		super(props);
		this.focusOnError = createDecorator(this.getFormInputs, findErrorInput);
		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		return {} as unknown as State;
	}

	protected abstract initValues(): FormFields;

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	protected isEqual(a: FormFields, b: FormFields): boolean {
		return JSON.stringify(a) === JSON.stringify(b);
	}

	protected getStepProps(): StepByStepContainerContextType & {
		stepName: string;
	} {
		const { activeStep, ...context } = this.context;
		const { stepName } = this.props;

		return {
			...context,
			stepName,
			activeStep: activeStep || stepName,
		};
	}

	protected isDisabled(): boolean {
		return false;
	}

	protected getCommonEventParameters(): Partial<GA4EventParameters> {
		const { ga4Event = {} } = this.context;

		return {
			...ga4Event,
			...super.getCommonEventParameters(),
		};
	}

	public render(): ReactNode {
		const { stepName, activeStep, activeAnim, stepNumber = 0 } = this.getStepProps();
		const cardClassName = activeStep === stepName ? compact([this.getClassName(), activeAnim]).join(" ") : "d-none";

		return (
			<FinalForm<FormFields>
				initialValuesEqual={this.isEqual}
				initialValues={this.initValues()}
				decorators={[this.focusOnError]}
				onSubmit={this.onSubmit}
			>
				{({ handleSubmit, values, form }) => {
					this.form = form;

					return (
						<Form
							ref={this.formRef}
							className={cardClassName}
							onSubmit={(element) => {
								const { hasValidationErrors, errors } = form.getState();

								if (hasValidationErrors) {
									this.sendEvent("BM_stepInvalid", {
										buttonId: GoogleAnalytics4.generateId("bi"),
										stepName,
										stepNumber,
										...errors,
									});
								}

								return handleSubmit(element);
							}}
							noValidate
						>
							<FormSpy
								subscription={{ touched: true }}
								onChange={(props: FormState<FormFields>) => {
									if (Object.values(props.touched).includes(true)) {
										this.onChange(props.values);
									}
								}}
							/>
							{this.renderContent(values, form)}
						</Form>
					);
				}}
			</FinalForm>
		);
	}

	protected renderContent(_values: FormFields, _form: FormApi<FormFields>): ReactNode {
		const {
			buttonBackText = "Vissza",
			buttonNextText = "Tovább",
			buttonNextFaIcon = <FontAwesomeIcon className="ml-2" icon={fasArrowRight} />,
			buttonBackFaIcon = (
				<FontAwesomeIcon className="mr-2 font-weight-normal" style={{ color: "#adb5bd" }} icon={fasArrowLeft} />
			),
			submitState,
			stepNumber,
			previousStep,
			showBackButton,
			modifiedProgressButtonSize,
		} = this.props;

		const nextButtonIconPosition = "end";
		const formattedButtonNextText = buttonNextText + " ".repeat(3);
		const formattedButtonBackText = " ".repeat(3) + buttonBackText;

		return (
			<Container>
				<Row className="align-items-start justify-content-start">
					<Col className="mb-4" xl={6} lg={7}>
						{this.renderFields()}
						<div className="d-flex">
							{stepNumber > 1 && showBackButton && (
								<Button
									id="buttonBack"
									variant="light"
									size="lg"
									className="mt-4 ml-2 font-weight-bold px-2 w-50"
									onClick={() => {
										this.onStep(previousStep, false);
									}}
								>
									{buttonBackFaIcon}
									<span className="font-weight-bold">{formattedButtonBackText} </span>
								</Button>
							)}
							<ProgressButton
								type="submit"
								id="buttonNext"
								className={`mt-4 ml-2 font-weight-bold px-2 ${stepNumber < 2 && modifiedProgressButtonSize ? "w-100" : "w-50"}`}
								icon={buttonNextFaIcon}
								iconPosition={nextButtonIconPosition}
								size="lg"
								state={submitState}
							>
								{formattedButtonNextText}
							</ProgressButton>
						</div>
					</Col>
					<Col className="mb-4" xl={6} lg={5}>
						{this.renderInfoBlock()}
					</Col>
				</Row>
			</Container>
		);
	}

	protected abstract renderFields(): ReactNode;
	protected abstract renderInfoBlock(): ReactNode;

	@autobind
	protected onSubmit(data: FormFields): void {
		const { onStep } = this.context;
		const { defaultTarget } = this.props;

		if (onStep) {
			onStep(this.target || defaultTarget, data);
		}
		this.target = undefined;
	}

	@autobind
	protected onChange(data: FormFields): void {
		const { onChange } = this.context;
		const { stepName } = this.props;

		if (onChange) {
			onChange(stepName, data);
		}
	}

	@autobind
	protected onStep(target: string, needSubmit = true): void {
		this.target = target;

		if (needSubmit) {
			this.form.submit()?.catch((error) => logError(error));
		} else {
			this.onSubmit(this.form.getState().values);
		}
	}

	protected validator(fieldName: string, cb: Function): string {
		const ret = cb();
		const { errors } = this;
		const index = errors.indexOf(fieldName);

		if (ret !== undefined) {
			if (index === -1) {
				errors.push(fieldName);
			}
		} else if (index > -1) {
			errors.splice(index, 1);
		}
		this.errors = errors;

		if (errors.length > 0) {
			this.props.hasError(this.props.stepName);
		}

		return ret;
	}

	protected getClassName(): string {
		const { className } = this.props;

		return compact(["step-by-step-form", className]).join(" ");
	}

	@autobind
	private getFormInputs(): HTMLInput[] {
		const form = this.formRef?.current;

		if (typeof document === "undefined" || !form) {
			return [];
		}

		return Array.prototype.slice.call(form).filter(isFocusableInput);
	}
}
